/**
 * API client — works with real backend or mock data in dev mode.
 * Uses Keycloak OIDC tokens from auth.js for Authorization header.
 */

import { getToken, refreshToken, login, isAuthenticated } from './auth';
import type { z } from 'zod';

const BASE: string = import.meta.env.VITE_API_BASE || '/api';
let USE_MOCK: boolean = false;

interface RequestOptions {
    method?: string;
    body?: any;
    headers?: Record<string, string>;
    signal?: AbortSignal;
}

// Auto-detect: if API not available, use mock (only on localhost)
function _isLocalDev(): boolean {
    const host = window.location.hostname;
    return host === 'localhost' || host === '127.0.0.1';
}

// Lazy-load mock module only when needed
let _mockModule: typeof import('./mock-data') | null = null;
async function getMockModule(): Promise<typeof import('./mock-data')> {
    if (!_mockModule) _mockModule = await import('./mock-data');
    return _mockModule;
}

async function request<T = any>(path: string, options: RequestOptions = {}, _retried: boolean = false): Promise<T> {
    if (USE_MOCK) {
        const m = await getMockModule();
        return m.mockRequest(path, options) as Promise<T>;
    }

    // Ensure trailing slash for FastAPI compatibility (avoid 307 redirects)
    // but not for paths with query strings
    if (!path.includes('?') && !path.endsWith('/')) {
        path = path + '/';
    } else if (path.includes('?') && !path.split('?')[0].endsWith('/')) {
        const qIdx = path.indexOf('?');
        path = path.slice(0, qIdx) + '/' + path.slice(qIdx);
    }

    const headers: Record<string, string> = { ...options.headers };

    // Attach Bearer token from Keycloak auth
    const token = getToken();
    if (token) {
        headers['Authorization'] = `Bearer ${token}`;
    }

    if (options.body && typeof options.body === 'object' && !(options.body instanceof FormData)) {
        headers['Content-Type'] = 'application/json';
        options.body = JSON.stringify(options.body);
    }

    try {
        const resp = await fetch(`${BASE}${path}`, { ...options, headers });
        if (!resp.ok) {
            if (resp.status === 401) {
                // Try token refresh once before giving up
                if (!_retried) {
                    const newToken = await refreshToken();
                    if (newToken) {
                        // Retry the request with the refreshed token
                        // Reset body if it was stringified (need original for retry)
                        const retryOpts = { ...options };
                        delete retryOpts.body; // will be re-stringified from original
                        return request<T>(
                            path.replace(/\/$/, ''),
                            { ...options, headers: { ...options.headers } },
                            true,
                        );
                    }
                }

                // Refresh failed or already retried
                if (_isLocalDev()) {
                    console.warn(`API 401 on ${path} — falling back to mock (localhost dev mode)`);
                    USE_MOCK = true;
                    const m = await getMockModule();
                    return m.mockRequest(path, options) as Promise<T>;
                }
                console.error(`API 401 on ${path} — redirecting to Keycloak login`);
                const err401: any = new Error('Authentication required');
                err401.status = 401;
                login();
                throw err401;
            }
            const err = await resp.json().catch(() => ({ detail: resp.statusText }));
            const e: any = new Error(err.detail || 'API error');
            e.status = resp.status;
            throw e;
        }
        return resp.json() as Promise<T>;
    } catch (e: any) {
        // Network errors: ONLY fall back to mock in dev mode.
        // In production, a network failure on /webrtc/credentials must NOT
        // return mock credentials — that would give JsSIP a fake password,
        // and subsequent REGISTER attempts would trigger Kamailio's auth
        // failure counter and ban the whole office NAT IP.
        if (e instanceof TypeError || e.name === 'AbortError') {
            if (_isLocalDev()) {
                console.warn(`API network error on ${path} — falling back to mock (dev mode)`);
                USE_MOCK = true;
                const m = await getMockModule();
                return m.mockRequest(path, options) as Promise<T>;
            }
            console.error(`API network error on ${path}:`, (e instanceof Error ? e.message : String(e)));
            // Show friendly toast
            const tc = document.getElementById('toast-container');
            if (tc && !tc.querySelector('.net-err-toast')) {
                const t = document.createElement('div');
                t.className = 'toast error net-err-toast';
                t.textContent = 'Немає з\'єднання з сервером. Перевірте мережу.';
                tc.appendChild(t);
                setTimeout(() => { t.style.opacity = '0'; setTimeout(() => t.remove(), 300); }, 6000);
            }
        }
        // Report to Sentry/GlitchTip
        import('./sentry').then((s) => s.captureException(e)).catch(() => {});
        throw e;
    }
}

/** Validate API response with Zod schema (soft — logs mismatch, returns data anyway) */
async function validated<T>(data: T, schema?: z.ZodType<T>): Promise<T> {
    if (!schema) return data;
    const result = schema.safeParse(data);
    if (!result.success) {
        import('./sentry').then((s) => {
            s.addBreadcrumb('zod', `API validation: ${result.error.issues.length} issues`, 'warning');
        }).catch(() => {});
    }
    return data;
}

export interface ApiClient {
    init(): Promise<void>;
    get<T = any>(path: string, schema?: z.ZodType<T>): Promise<T>;
    post<T = any>(path: string, body?: any): Promise<T>;
    put<T = any>(path: string, body?: any): Promise<T>;
    delete<T = any>(path: string): Promise<T>;
    isMock(): boolean;
}

export const api: ApiClient = {
    async init(): Promise<void> {
        try {
            const resp = await fetch(`${BASE}/system/health/`, { signal: AbortSignal.timeout(2000) });
            if (resp.ok) {
                USE_MOCK = false;
            } else {
                console.warn('API health check failed — using mock data');
                USE_MOCK = true;
            }
        } catch (e: unknown) {
            USE_MOCK = true;
        }
        if (USE_MOCK) console.warn('API: using mock data (backend unavailable)');
    },
    get: async <T = any>(path: string, schema?: z.ZodType<T>): Promise<T> => validated(await request<T>(path), schema),
    post: <T = any>(path: string, body?: any): Promise<T> => request<T>(path, { method: 'POST', body }),
    put: <T = any>(path: string, body?: any): Promise<T> => request<T>(path, { method: 'PUT', body }),
    delete: <T = any>(path: string): Promise<T> => request<T>(path, { method: 'DELETE' }),
    isMock: (): boolean => USE_MOCK,
};

// Re-export mock data for pages that need it directly
export { MOCK_USERS, MOCK_CDR, MOCK_RING_GROUPS, MOCK_DEPARTMENTS } from './mock-data';
